import { useState, useRef, useEffect } from "react";
import { User, Loader2, CheckCircle2, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/hooks/useAuth";
import { useChurch } from "@/hooks/useChurch";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

export default function ProfilePage() {
  const { user, role } = useAuth();
  const { profile } = useChurch();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- Avatar ---
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  const { data: userProfile } = useQuery({
    queryKey: ["user_profile", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("display_name, avatar_url, personal_email, personal_phone")
        .eq("user_id", user!.id)
        .maybeSingle() as any;
      if (error) throw error;
      return data as {
        display_name: string | null;
        avatar_url: string | null;
        personal_email: string | null;
        personal_phone: string | null;
      } | null;
    },
    enabled: !!user?.id,
  });

  const avatarUrl = userProfile?.avatar_url || null;

  // --- Name Fields ---
  const [title, setTitle] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [displayName, setDisplayName] = useState((user as any)?.user_metadata?.display_name || user?.email?.split("@")[0] || "");
  useEffect(() => {
    // Try to split displayName into title, first, last if possible
    if (userProfile?.display_name) {
      const name = userProfile.display_name;
      const titles = ["Rev. Dr.", "Mr.", "Ms.", "Rev.", "Dr.", "Pastor"];
      const matchedTitle = titles.find((t) => name.startsWith(t + " "));
      if (matchedTitle) {
        const rest = name.slice(matchedTitle.length + 1).split(" ");
        setTitle(matchedTitle);
        setFirstName(rest[0] || "");
        setLastName(rest.slice(1).join(" ") || "");
      } else {
        const parts = name.split(" ");
        setTitle("");
        setFirstName(parts[0] || "");
        setLastName(parts.slice(1).join(" ") || "");
      }
      setDisplayName(name);
    }
  }, [userProfile?.display_name]);
  const [nameLoading, setNameLoading] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);
  const [personalEmail, setPersonalEmail] = useState("");
  const [personalPhone, setPersonalPhone] = useState("");
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSaved, setContactSaved] = useState(false);

  useEffect(() => {
    setPersonalEmail(userProfile?.personal_email || user?.email || "");
    setPersonalPhone(userProfile?.personal_phone || "");
  }, [userProfile?.personal_email, userProfile?.personal_phone, user?.email]);

  const initials = displayName
    .split(" ")
    .map((n: string) => n[0]?.toUpperCase())
    .join("")
    .slice(0, 2) || user?.email?.[0]?.toUpperCase() || "U";

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: "Invalid file type", description: "Please upload a JPG, PNG, WebP, or GIF image.", variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File too large", description: "Image must be under 5 MB.", variant: "destructive" });
      return;
    }
    setAvatarUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `avatars/${user.id}/avatar.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("church-assets")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from("church-assets").getPublicUrl(path);
      // Append cache-busting param so the browser reloads the image
      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      const { error: dbError } = await (supabase
        .from("profiles") as any)
        .update({ avatar_url: publicUrl })
        .eq("user_id", user.id);
      if (dbError) throw dbError;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["user_profile", user.id] }),
        queryClient.invalidateQueries({ queryKey: ["user_profile_header", user.id] }),
      ]);
      toast({ title: "Photo updated", description: "Your profile photo has been saved." });
    } catch (err: any) {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    }
    setAvatarUploading(false);
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSaveName = async () => {
    if (!displayName.trim()) return;
    setNameLoading(true);
    setNameSaved(false);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ display_name: displayName.trim() })
        .eq("user_id", user!.id);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["user_profile", user?.id] });
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 2500);
      toast({ title: "Name updated", description: "Your display name has been saved." });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setNameLoading(false);
  };



  const handleCopyProfileUrl = () => {
    const url = `${window.location.origin}/profile`;
    navigator.clipboard.writeText(url);
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2000);
    toast({ title: "Link copied", description: "Profile URL copied to clipboard" });
  };

  const handleSaveContact = async () => {
    if (!user?.id) return;

    setContactLoading(true);
    setContactSaved(false);
    try {
      const normalizedEmail = personalEmail.trim();
      if (normalizedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
        toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
        setContactLoading(false);
        return;
      }

      const { error } = await (supabase
        .from("profiles") as any)
        .update({
          personal_email: normalizedEmail || null,
          personal_phone: personalPhone.trim() || null,
        })
        .eq("user_id", user.id);

      if (error) throw error;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["user_profile", user.id] }),
        queryClient.invalidateQueries({ queryKey: ["user_profile_header", user.id] }),
      ]);
      setContactSaved(true);
      setTimeout(() => setContactSaved(false), 2500);
      toast({ title: "Contact updated", description: "Your personal email and phone have been saved." });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setContactLoading(false);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
          <User className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Profile</h1>
          <p className="text-sm text-muted-foreground">Manage your profile information and preferences</p>
        </div>
      </div>

      {/* Profile Photo Section */}
      <section className="border-y border-border py-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex justify-center sm:justify-start flex-shrink-0">
            <div className="relative">
              <Avatar className="w-24 h-24 border-4 border-background shadow-lg">
                <AvatarImage src={avatarUrl ?? undefined} alt={displayName} className="object-cover" />
                <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-bold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={avatarUploading}
                className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md hover:bg-primary/90 transition-colors ring-4 ring-background"
                title="Change photo"
              >
                {avatarUploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4" />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleAvatarChange}
                className="hidden"
              />
            </div>
          </div>

          <div className="flex-1 text-center sm:text-left">
            <h2 className="font-semibold text-lg text-foreground">{displayName || "User"}</h2>
            {profile?.name && (
              <p className="text-sm text-muted-foreground mb-3">{profile.name}</p>
            )}
            {role && (
              <Badge variant="secondary" className="text-xs capitalize px-3 py-1">
                {role}
              </Badge>
            )}
          </div>
        </div>
      </section>

      {/* Display Name Section */}
      <section className="py-6 border-b border-border">
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-1">Display Information</h2>
          <p className="text-sm text-muted-foreground">Update your name and title</p>
        </div>

        <div className="space-y-4 mt-4">
          <div className="flex flex-col md:flex-row md:items-end gap-2">
            <div className="space-y-2 md:max-w-[120px]">
              <Label className="text-sm font-medium">Title</Label>
              <Select value={title || "__none"} onValueChange={v => { setTitle(v === "__none" ? "" : v); setNameSaved(false); }}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select title" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">None</SelectItem>
                  <SelectItem value="Mr.">Mr.</SelectItem>
                  <SelectItem value="Ms.">Ms.</SelectItem>
                  <SelectItem value="Rev.">Rev.</SelectItem>
                  <SelectItem value="Dr.">Dr.</SelectItem>
                  <SelectItem value="Rev. Dr.">Rev. Dr.</SelectItem>
                  <SelectItem value="Pastor">Pastor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 space-y-2">
              <Label className="text-sm font-medium">First Name</Label>
              <Input
                value={firstName}
                onChange={e => { setFirstName(e.target.value); setNameSaved(false); }}
                placeholder="First name"
                className="w-full"
                maxLength={40}
              />
            </div>

            <div className="flex-1 space-y-2">
              <Label className="text-sm font-medium">Last Name</Label>
              <Input
                value={lastName}
                onChange={e => { setLastName(e.target.value); setNameSaved(false); }}
                placeholder="Last name"
                className="w-full"
                maxLength={40}
              />
            </div>
          </div>

          <Button
            onClick={() => {
              setNameLoading(true);
              const newDisplayName = [title, firstName, lastName].filter(Boolean).join(" ");
              // Save to supabase (update display_name)
              supabase.from("profiles").update({ display_name: newDisplayName }).eq("user_id", user.id).then(() => {
                setDisplayName(newDisplayName);
                setNameLoading(false);
                setNameSaved(true);
                Promise.all([
                  queryClient.invalidateQueries({ queryKey: ["user_profile", user?.id] }),
                  queryClient.invalidateQueries({ queryKey: ["user_profile_header", user?.id] }),
                ]);
              });
            }}
            disabled={nameLoading || !firstName.trim() || !lastName.trim()}
            className="w-full sm:w-auto"
          >
            {nameLoading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : nameSaved ? (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Saved
              </>
            ) : (
              "Update Profile"
            )}
          </Button>
        </div>
      </section>

      {/* Personal Contact Section */}
      <section className="py-6 border-b border-border">
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-1">Personal Contact</h2>
          <p className="text-sm text-muted-foreground">Update your personal email and phone number</p>
        </div>

        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Personal Email</Label>
              <Input
                type="email"
                value={personalEmail}
                onChange={(e) => { setPersonalEmail(e.target.value); setContactSaved(false); }}
                placeholder="you@example.com"
                className="w-full"
                maxLength={120}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Phone Number</Label>
              <Input
                value={personalPhone}
                onChange={(e) => { setPersonalPhone(e.target.value); setContactSaved(false); }}
                placeholder="(555) 123-4567"
                className="w-full"
                maxLength={30}
              />
            </div>
          </div>

          <Button
            onClick={handleSaveContact}
            disabled={contactLoading}
            className="w-full sm:w-auto"
          >
            {contactLoading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : contactSaved ? (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Saved
              </>
            ) : (
              "Update Contact"
            )}
          </Button>
        </div>
      </section>
    </div>
  );
}
